// src/components/Footer.jsx
import React, { useState, useEffect } from 'react';
import { FaUniversalAccess, FaFacebookF, FaInstagram, FaXTwitter, FaTiktok } from 'react-icons/fa6';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AccessibilityMenu from './AccessibilityMenu'; // Asegura la ruta correcta

export default function Footer({ language = "en" }) {
  // Estado persistente del modo accesible
  const [accessibleMode, setAccessibleMode] = useState(
    () => JSON.parse(localStorage.getItem('accessibleMode')) || false
  );

  // Mostrar/ocultar panel de accesibilidad
  const [showAccessibilityMenu, setShowAccessibilityMenu] = useState(false);

  // Estado del modal legal (lo mantengo por si lo usas más adelante)
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalType, setLegalType] = useState(null); // 'terms' | 'returns' | 'privacy'

  const navigate = useNavigate();
  const location = useLocation();

  // Persistir preferencia y aplicar clases globales al <html>
  useEffect(() => {
    localStorage.setItem('accessibleMode', JSON.stringify(accessibleMode));
    const root = document.documentElement; // <html>
    root.classList.toggle('high-contrast', accessibleMode);
    root.classList.toggle('large-text', accessibleMode);
    root.classList.toggle('reduced-motion', accessibleMode);
  }, [accessibleMode]);

  const footerTexts = {
    contacto: { en: 'Contact', es: 'Contacto', pt: 'Contato' },
    faq: { en: 'FAQs', es: 'FAQs', pt: 'FAQs' },
    terminos: { en: 'Terms of Use', es: 'Términos de uso', pt: 'Termos de uso' },
    devoluciones: { en: 'Return Policy', es: 'Política de devoluciones', pt: 'Política de devoluções' },
    privacidad: { en: 'Privacy Policy', es: 'Política de privacidad', pt: 'Política de privacidade' },
    accessibility: { en: 'Accessibility', es: 'Accesibilidad', pt: 'Acessibilidade' },
	modoAccesible: { en: 'Accessible Mode', es: 'Modo accesible', pt: 'Modo acessível' },
    modoEstandar: { en: 'Standard Mode', es: 'Modo estándar', pt: 'Modo padrão' },
    accessibleAriaLabelOn: {
      en: "Disable accessible mode",
      es: "Desactivar modo accesible",
      pt: "Desativar modo acessível"
    },
    accessibleAriaLabelOff: {
      en: "Enable accessible mode",
      es: "Activar modo accesible",
      pt: "Ativar modo acessível"
    },
  };

  // (Mantengo estas estructuras por si vuelves a usar el modal)
  const legalTitles = {
    terms: { en: 'Terms of Use', es: 'Términos de uso', pt: 'Termos de uso' },
    returns: { en: 'Return Policy', es: 'Política de devoluciones', pt: 'Política de devoluções' },
    privacy: { en: 'Privacy Policy', es: 'Política de privacidad', pt: 'Política de privacidade' }
  };

  const legalContent = {
    terms: {
      en: ['Terms of Use. Terms of Use. Terms of Use. Terms of Use.'],
      es: ['Términos de uso. Términos de uso. Términos de uso. Términos de uso.'],
      pt: ['Termos de uso. Termos de uso. Termos de uso. Termos de uso.']
    },
    returns: {
      en: ['Return Policy. Return Policy. Return Policy. Return Policy.'],
      es: ['Política de devoluciones. Política de devoluciones. Política de devoluciones. Política de devoluciones.'],
      pt: ['Política de devoluções. Política de devoluções. Política de devoluções. Política de devoluções.']
    },
    privacy: {
      en: [
        'Privacy Policy — Pre-Launch Phase',
        'Last updated: November 2025',
        'YourLostAndFound is currently in a pre-launch phase. During this period, we collect only minimal information from visitors who voluntarily join our waitlist or contact us through our forms.',
        'What we collect: Full name and email address (for communication purposes).',
        'How we use this information: To provide updates about our upcoming launch and related news; to respond to direct inquiries submitted via the contact form.',
        'We do not share, sell, or transfer your data to third parties. All information is stored securely and will be handled according to our full Privacy Policy once the platform becomes operational in 2026.',
        'If you wish to have your information deleted before our launch, please contact: privacy@yourlostandfound.com'
      ],
      es: [
        'Política de Privacidad — Fase Pre-Lanzamiento',
        'Última actualización: Noviembre 2025',
        'YourLostAndFound se encuentra en fase previa al lanzamiento. Durante este periodo, recopilamos únicamente la información mínima de los visitantes que se registran voluntariamente en la lista de espera o nos contactan mediante formularios.',
        'Información que recopilamos: Nombre completo y correo electrónico (con fines de comunicación).',
        'Uso de la información: Enviar actualizaciones sobre el lanzamiento y noticias relacionadas; responder consultas enviadas a través del formulario de contacto.',
        'No compartimos, vendemos ni transferimos tu información a terceros. Los datos se almacenan de forma segura y serán tratados conforme a nuestra Política de Privacidad completa cuando la plataforma esté operativa en 2026.',
        'Si deseas eliminar tu información antes del lanzamiento, contáctanos en: privacy@yourlostandfound.com'
      ],
      pt: [
        'Política de Privacidade — Fase Pré-Lançamento',
        'Última atualização: Novembro de 2025',
        'O YourLostAndFound está em fase pré-lançamento. Durante este período, coletamos apenas informações mínimas de visitantes que se cadastram voluntariamente na lista de espera ou entram em contato por meio de formulários.',
        'Informações coletadas: Nome completo e endereço de e-mail (para fins de comunicação).',
        'Como usamos as informações: Enviar atualizações sobre o lançamento e notícias relacionadas; responder às mensagens enviadas pelo formulário de contato.',
        'Não compartilhamos, vendemos nem transferimos seus dados a terceiros. As informações são armazenadas com segurança e serão tratadas conforme nossa Política de Privacidade completa quando a plataforma estiver ativa em 2026.',
        'Se desejar excluir seus dados antes do lançamento, entre em contato: privacy@yourlostandfound.com'
      ]
    }
  };

  const openLegal = (type) => {
    setLegalType(type);
    setLegalModalOpen(true);
  };
  const closeLegal = () => {
    setLegalModalOpen(false);
    setLegalType(null);
  };

  const legalTitle = legalType ? legalTitles[legalType][language] : '';
  const legalBody = legalType ? legalContent[legalType][language] : [];

  return (
    <footer className="bg-gray-100 py-6 px-6 border-t border-gray-300" role="contentinfo">
      <div className="max-w-screen-2xl mx-auto flex flex-col sm:flex-row justify-between items-center text-sm text-gray-600">

        {/* Redes Sociales */}
        <div className="flex space-x-4 mb-4 sm:mb-0">
          <a
            href="https://www.facebook.com/yourlostandfound"
            target="_blank" rel="noopener noreferrer"
            aria-label="Facebook"
            className="hover:text-black focus:outline-none focus:ring-2 focus:ring-turquoise-400 rounded"
            tabIndex={0}
          >
            <FaFacebookF className="text-xl" aria-hidden="true" />
          </a>

          <a
            href="https://www.instagram.com/yourlostandfoundqr/"
            target="_blank" rel="noopener noreferrer"
            aria-label="Instagram"
            className="hover:text-black focus:outline-none focus:ring-2 focus:ring-turquoise-400 rounded"
            tabIndex={0}
          >
            <FaInstagram className="text-xl" aria-hidden="true" />
          </a>

          <a
            href="https://x.com/ylf_QR"
            target="_blank" rel="noopener noreferrer"
            aria-label="X (Twitter)"
            className="hover:text-black focus:outline-none focus:ring-2 focus:ring-turquoise-400 rounded"
            tabIndex={0}
          >
            <FaXTwitter className="text-xl" aria-hidden="true" />
          </a>

          <a
            href="https://www.tiktok.com/@yourlostandfound.com"
            target="_blank" rel="noopener noreferrer"
            aria-label="TikTok"
            className="hover:text-black focus:outline-none focus:ring-2 focus:ring-turquoise-400 rounded"
            tabIndex={0}
          >
            <FaTiktok className="text-xl" aria-hidden="true" />
          </a>
        </div>

        {/* Políticas + Accesibilidad */}
        <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0 items-center text-center sm:text-left">
          <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0">
            <Link to="/contact" className="hover:underline focus:outline-none focus:ring-2 focus:ring-turquoise-400 rounded">
              {footerTexts.contacto[language]}
            </Link>

            <button
              onClick={() => {
                if (location.pathname === '/') {
                  document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  navigate('/#faq');
                }
              }}
              className="bg-transparent text-inherit border-none p-0 m-0 hover:underline focus:outline-none focus:ring-2 focus:ring-turquoise-400 rounded"
              aria-label="Go to FAQs section"
            >
              {footerTexts.faq[language]}
            </button>

            {/* Navegación legal como páginas */}
            <Link to="/terms" className="hover:underline focus:outline-none focus:ring-2 focus:ring-turquoise-400 rounded">
              {footerTexts.terminos[language]}
            </Link>

            <Link to="/privacy" className="hover:underline focus:outline-none focus:ring-2 focus:ring-turquoise-400 rounded">
              {footerTexts.privacidad[language]}
            </Link>
			 <Link to="/Accessibility" className="hover:underline focus:outline-none focus:ring-2 focus:ring-turquoise-400 rounded">
              {footerTexts.accessibility[language]}
            </Link>
          </div>

          {/* Botón Modo Accesible */}
          <div className="relative">
            <button
  onClick={() => setShowAccessibilityMenu(prev => !prev)}
  className="flex items-center bg-white/80 hover:bg-white/90 text-gray-700 font-medium py-2 px-4 rounded transition focus:outline-none focus:ring-4 focus:ring-turquoise-400 duration-300 ease-in-out mt-4 sm:mt-0"
  aria-haspopup="dialog"
  aria-expanded={showAccessibilityMenu}
  aria-controls="a11y-menu"
  aria-label={footerTexts.accessibility[language]}
  title={footerTexts.accessibility[language]}
>
              <FaUniversalAccess className="mr-2 text-xl" aria-hidden="true" />
              {accessibleMode
                ? footerTexts.modoEstandar[language]
                : footerTexts.modoAccesible[language]}
            </button>

            {/* Panel de accesibilidad */}
            {showAccessibilityMenu && (
              <div id="a11y-menu" className="absolute right-0 mt-2">
                <AccessibilityMenu />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Legal Reutilizable (lo conservo por si lo reactivas) */}
      {legalModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="legal-modal-title"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6">
            <h2 id="legal-modal-title" className="text-xl font-semibold mb-4">
              {legalTitle}
            </h2>

            <div className="text-gray-700 leading-relaxed mb-6">
              {Array.isArray(legalBody)
                ? legalBody.map((p, i) => (
                    <p key={i} className={i === 0 ? 'font-semibold mb-2' : 'mb-3'}>
                      {p}
                    </p>
                  ))
                : <p>{legalBody}</p>}
            </div>

            <div className="flex justify-end">
              <button
                onClick={closeLegal}
                className="inline-flex items-center rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-turquoise-400"
              >
                {language === 'es' ? 'Cerrar' : language === 'pt' ? 'Fechar' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
